/**
 * B1022 d.2 — the public CHECK A BALANCE page (845:2): the pure half. The
 * route, the field checks the page owes BEFORE the network (16 digits, the
 * Luhn digit, a 4-digit PIN), and `giftcard.balance`'s answer narrowed to the
 * words the page shows. Nothing here stores what the buyer typed.
 * Pure: no React, no I/O; the zone is passed in.
 */
import { formatMoney, type Money } from "./money.js";

export interface BalanceRoute {
  readonly tenantId?: string;
  readonly siteKey?: string;
}

/** `/gift-cards/balance?tenant=<id>` (or `?siteKey=`); anything else is not this page. */
export function balanceRouteFrom(loc: { readonly pathname: string; readonly search: string }): BalanceRoute | undefined {
  if (!/\/gift-cards\/balance\/?$/.test(loc.pathname)) return undefined;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(loc.search);
  } catch {
    params = new URLSearchParams();
  }
  const tenantId = params.get("tenant") ?? "";
  const siteKey = params.get("siteKey") ?? "";
  return { ...(tenantId !== "" ? { tenantId } : {}), ...(siteKey !== "" ? { siteKey } : {}) };
}

/** The digits of a card number as people type it (spaces and hyphens dropped). */
export function cardDigits(text: string): string {
  return text.replace(/[\s-]/g, "");
}

/** The Luhn check — the same one the door runs, so a typo never costs a try. */
export function luhnOk(digits: string): boolean {
  if (!/^\d{2,}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

/**
 * The PIN's one note. RULED (class B, the design seat's default — overrule
 * with "no on B1022 tries-left"): no per-card tries-left count — a count
 * shown only for real cards would tell a stranger which numbers exist.
 */
export const PIN_NOTE = "4 digits, from the email. Three wrong PINs lock a card for an hour.";

export type BalanceIssues = Partial<Record<"number" | "pin", string>>;

/** Every refusal the page owes before it asks the door — each beside its own field. */
export function balanceIssues(input: { readonly number: string; readonly pin: string }): BalanceIssues {
  const out: { number?: string; pin?: string } = {};
  const digits = cardDigits(input.number);
  if (input.number.trim() === "") out.number = "Enter the 16-digit number from the gift card email.";
  else if (!/^\d{16}$/.test(digits)) out.number = "A gift card number is 16 digits.";
  else if (!luhnOk(digits)) out.number = "Check the card number — one of the digits is off.";
  const pin = input.pin.trim();
  if (pin === "") out.pin = "Enter the 4-digit PIN from the email.";
  else if (!/^\d{4}$/.test(pin)) out.pin = "The PIN is 4 digits.";
  return out;
}

export interface BalanceHistoryRow {
  readonly kind: string;
  readonly when: string;
  readonly amount: string;
}

export interface BalanceFacts {
  /** AVAILABLE NOW's figure. */
  readonly available: string;
  /** "of $50.00 loaded · •••• 8903" — the last four only, never the number. */
  readonly ofLoaded: string;
  /** Whether USE IT ONLINE is offered (an active card with money on it). */
  readonly usable: boolean;
  /** Why it can't be used, when it can't. */
  readonly note?: string;
  /** Newest first. */
  readonly history: readonly BalanceHistoryRow[];
}

const isMoney = (m: unknown): m is Money =>
  typeof m === "object" && m !== null && Number.isInteger((m as Money).amount) && typeof (m as Money).currency === "string";

const KIND_WORDS: Readonly<Record<string, string>> = { loaded: "Loaded", used: "Used", refunded: "Refunded", adjusted: "Adjusted" };

const STATUS_NOTES: Readonly<Record<string, string>> = {
  frozen: "This card is paused by the business — ask them about it before you use it.",
  scheduled: "This card hasn't been sent yet — it works from the day it arrives.",
  used_up: "This card is used up.",
};

function dayWords(at: number, timeZone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric", year: "numeric" }).format(at);
  } catch {
    return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" }).format(at);
  }
}

/** `giftcard.balance`'s answer → the page's words; anything malformed reads as no answer. */
export function balanceFacts(res: unknown, opts: { readonly timeZone?: string | undefined }): BalanceFacts | undefined {
  if (typeof res !== "object" || res === null) return undefined;
  const r = res as Record<string, unknown>;
  const status = typeof r["status"] === "string" ? r["status"] : undefined;
  if (typeof r["lastFour"] !== "string" || !/^\d{4}$/.test(r["lastFour"]) || status === undefined) return undefined;
  if (!isMoney(r["balance"]) || !isMoney(r["loaded"])) return undefined;
  const balance = r["balance"];
  const rows: { at: number; row: BalanceHistoryRow }[] = [];
  for (const h of Array.isArray(r["history"]) ? (r["history"] as unknown[]) : []) {
    const e = (typeof h === "object" && h !== null ? h : {}) as Record<string, unknown>;
    const kind = typeof e["kind"] === "string" ? KIND_WORDS[e["kind"]] : undefined;
    if (kind === undefined || typeof e["at"] !== "number" || !isMoney(e["amount"])) continue;
    /* used rides as a positive spend; an adjustment carries its own sign (giftCardHistory) */
    const credit = e["kind"] === "used" ? false : e["kind"] === "adjusted" ? e["amount"].amount >= 0 : true;
    const face = formatMoney({ amount: Math.abs(e["amount"].amount), currency: e["amount"].currency });
    rows.push({ at: e["at"], row: { kind, when: dayWords(e["at"], opts.timeZone), amount: `${credit ? "+" : "−"}${face}` } });
  }
  rows.sort((a, b) => b.at - a.at);
  const note = STATUS_NOTES[status];
  return {
    available: formatMoney(balance),
    ofLoaded: `of ${formatMoney(r["loaded"])} loaded · •••• ${r["lastFour"]}`,
    usable: status === "active" && balance.amount > 0,
    ...(note !== undefined ? { note } : {}),
    history: rows.map((x) => x.row),
  };
}
